import { useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Info, Plus, Trash2, X } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { usePatientDialog } from '../patients/use-patient-dialog'
import {
  absorbingGroup,
  addGroupAt,
  ageGroupLabel,
  deleteGroup,
  editsByFirstAge,
  firstAgeBounds,
  groupHolding,
  lowerFirst,
  moveFirstAge,
  newGroupAgeError,
  sortedGroups,
  suggestedWindow,
  timelineRange,
  windowIssues,
  type AgeGroupDraft,
  type AgeWindowDraft,
} from './age-scheduling-model'
import { AgeTimeline, AgeTimelineAxis } from './AgeTimeline'
import { AGE_SCHEDULING_MAX_WINDOWS } from './settings-contracts'
import panel from './SettingsSection.module.css'
import styles from './AgeSchedulingSettings.module.css'

export type AgeGroupDialogTarget =
  { kind: 'add' } | { kind: 'edit'; key: string }

/**
 * One age group: where it starts and when Flio can book it. Works on a copy
 * and hands back the whole new set of groups on Apply, so every group still
 * covers its ages; nothing is sent until Save age-based scheduling.
 */
export function AgeGroupDialog({
  target,
  groups,
  enabled,
  clinic,
  newKey,
  onApply,
  onCancel,
}: {
  target: AgeGroupDialogTarget
  groups: AgeGroupDraft[]
  enabled: boolean
  clinic: { start: string; end: string } | null
  newKey: () => string
  onApply: (groups: AgeGroupDraft[]) => void
  onCancel: () => void
}) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  const baseId = useId()
  const safeChoice = useRef<HTMLButtonElement>(null)
  const [mode, setMode] = useState<'edit' | 'discard' | 'delete'>('edit')

  const editing =
    target.kind === 'edit'
      ? groups.find((group) => group.key === target.key)
      : undefined
  const sorted = sortedGroups(groups)
  const byFirstAge = editsByFirstAge(groups)
  const isFirst = editing ? sorted[0]?.key === editing.key : false
  const bounds = editing ? firstAgeBounds(groups, editing.key) : null
  const showAge = byFirstAge && (editing ? !isFirst : groups.length > 0)

  const [addedKey] = useState(() => newKey())
  const [initial] = useState(() => ({
    firstAge: editing && showAge ? String(editing.minimumYears) : '',
    windows: editing
      ? editing.windows.map((window) => ({ ...window }))
      : [
          {
            key: newKey(),
            start: clinic?.start ?? '08:00',
            end: clinic?.end ?? '17:00',
          },
        ],
  }))
  const [firstAge, setFirstAge] = useState(initial.firstAge)
  const [windows, setWindows] = useState<AgeWindowDraft[]>(initial.windows)
  const changed =
    firstAge !== initial.firstAge ||
    JSON.stringify(windows) !== JSON.stringify(initial.windows)

  function ask(next: 'discard' | 'delete') {
    setMode(next)
    // After the question renders, its safe choice takes focus.
    window.setTimeout(() => safeChoice.current?.focus(), 0)
  }

  function requestClose() {
    if (changed) ask('discard')
    else onCancel()
  }

  usePatientDialog(dialog, {
    onClose: () => (mode === 'edit' ? requestClose() : setMode('edit')),
  })

  const age = firstAge.trim() === '' ? Number.NaN : Number(firstAge)
  const ageError = !showAge
    ? null
    : firstAge.trim() === ''
      ? editing
        ? 'Enter the age this group starts at.'
        : null
      : editing
        ? !Number.isInteger(age) ||
          !bounds ||
          age < bounds.min ||
          age > bounds.max
          ? `Choose an age from ${bounds?.min ?? 1} to ${bounds?.max ?? 130}.`
          : null
        : newGroupAgeError(groups, age)
  const issues = windowIssues(windows)
  const blocked =
    Boolean(ageError) ||
    (showAge && firstAge.trim() === '') ||
    issues.some(Boolean)

  // The groups as they'd be after Apply, when the entries are usable.
  const result = useMemo(() => {
    if (blocked) return null
    if (!editing) {
      if (!groups.length) {
        return [{ key: addedKey, minimumYears: 0, windows }]
      }
      return addGroupAt(groups, age, { key: addedKey, windows })
    }
    const moved =
      showAge && age !== editing.minimumYears
        ? moveFirstAge(groups, editing.key, age)
        : groups
    return moved.map((group) =>
      group.key === editing.key ? { ...group, windows } : group
    )
  }, [addedKey, age, blocked, editing, groups, showAge, windows])

  const subjectKey = editing?.key ?? addedKey
  const subject = result?.find((group) => group.key === subjectKey)
  const label = (group: AgeGroupDraft, list: AgeGroupDraft[]) =>
    ageGroupLabel(group, { alone: list.length === 1 })

  // What else changes: the neighbour that gives up or takes ages.
  let neighbour: string | null = null
  if (result && showAge) {
    const before = editing
      ? sorted[sorted.findIndex((group) => group.key === editing.key) - 1]
      : groupHolding(groups, age)
    const after = before && result.find((group) => group.key === before.key)
    if (before && after && after.maximumYears !== before.maximumYears) {
      neighbour = `${label(before, groups)} will become ${lowerFirst(label(after, result))}${editing ? '' : ' and keep their times'}.`
    }
  }

  const currentLabel = editing ? label(editing, groups) : null
  const lastGroup = groups.length === 1
  const deleteBlocked =
    editing && enabled && lastGroup
      ? 'Turn off age-based scheduling to delete the last group.'
      : null
  const absorber = editing ? absorbingGroup(groups, editing.key) : undefined
  const deleteConsequence = !editing
    ? ''
    : lastGroup
      ? 'No age groups will be left.'
      : byFirstAge && absorber
        ? `${currentLabel} will join ${lowerFirst(label(absorber, groups))} and use their times.`
        : `${currentLabel} will have no group.`

  const ids = {
    age: `${baseId}-age`,
    ageHint: `${baseId}-age-hint`,
    ageError: `${baseId}-age-error`,
    deleteHint: `${baseId}-delete-hint`,
  }
  const range = timelineRange(clinic, windows)
  const outsideClinic =
    clinic !== null &&
    windows.some(
      (window, index) =>
        !issues[index] &&
        (window.start < clinic.start || window.end > clinic.end)
    )

  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.dialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={panel.dialogHeader}>
          <div>
            <span className={panel.eyebrow}>
              {editing ? 'Age group' : 'New age group'}
            </span>
            <h2 id={titleId}>
              {editing
                ? `Edit ${lowerFirst(currentLabel ?? '')}`
                : 'Add age group'}
            </h2>
          </div>
          <button
            type="button"
            className={panel.closeButton}
            aria-label="Close"
            onClick={requestClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {mode === 'delete' && editing ? (
          <div className={panel.dialogBody}>
            <div
              className={styles.deletePanel}
              role="group"
              aria-labelledby={`${baseId}-delete`}
            >
              <span className={styles.deleteIcon} aria-hidden="true">
                <Trash2 size={18} />
              </span>
              <div>
                <h3 id={`${baseId}-delete`}>
                  Delete {lowerFirst(currentLabel ?? '')}?
                </h3>
                <p>{deleteConsequence}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className={panel.dialogBody}>
            {showAge ? (
              <div className={`${panel.field} ${styles.ageField}`}>
                <label htmlFor={ids.age} className={panel.fieldLabel}>
                  Starts at age
                </label>
                <input
                  id={ids.age}
                  data-autofocus
                  type="number"
                  inputMode="numeric"
                  min={editing ? bounds?.min : 1}
                  max={editing ? bounds?.max : 130}
                  step={1}
                  value={firstAge}
                  aria-invalid={Boolean(ageError)}
                  aria-describedby={`${ids.ageHint}${ageError ? ` ${ids.ageError}` : ''}`}
                  onChange={(event) => setFirstAge(event.currentTarget.value)}
                />
                <small
                  id={ids.ageHint}
                  className={panel.hint}
                  aria-live="polite"
                >
                  {subject
                    ? `Covers ${lowerFirst(label(subject, result!))}.`
                    : 'The group covers every age from here up to the next group.'}
                  {neighbour ? ` ${neighbour}` : ''}
                </small>
                {ageError ? (
                  <small
                    id={ids.ageError}
                    className={panel.fieldError}
                    role="alert"
                  >
                    <AlertTriangle size={14} aria-hidden="true" />
                    {ageError}
                  </small>
                ) : null}
              </div>
            ) : (
              <p className={styles.ageNote}>
                {!byFirstAge
                  ? 'Ages can be changed once every age has a group.'
                  : editing
                    ? lastGroup
                      ? 'Covers every age. Add an age group to give some ages different times.'
                      : 'The first group starts at age 0. Edit the next group to change where it ends.'
                    : 'This first group covers every age. Add more groups to give some ages different times.'}
              </p>
            )}

            <fieldset className={styles.times}>
              <legend className={styles.timesLegend}>Bookable times</legend>
              <p className={styles.timesHint}>
                Appointments must start and end within these times, every day
                the clinic is open.
              </p>
              <div className={styles.timeHeader} aria-hidden="true">
                <span>Starts</span>
                <span />
                <span>Ends</span>
              </div>
              <ul className={styles.timeRows}>
                {windows.map((window, index) => (
                  <li key={window.key}>
                    <div
                      className={styles.timeRow}
                      data-invalid={issues[index] ? '' : undefined}
                    >
                      <label className={styles.timeInput}>
                        <span className={panel.srOnly}>
                          Time {index + 1} starts
                        </span>
                        <input
                          type="time"
                          value={window.start}
                          aria-invalid={Boolean(issues[index])}
                          onChange={(event) => {
                            const start = event.currentTarget.value
                            setWindows((current) =>
                              current.map((item) =>
                                item.key === window.key
                                  ? { ...item, start }
                                  : item
                              )
                            )
                          }}
                        />
                      </label>
                      <span className={styles.dash} aria-hidden="true">
                        –
                      </span>
                      <label className={styles.timeInput}>
                        <span className={panel.srOnly}>
                          Time {index + 1} ends
                        </span>
                        <input
                          type="time"
                          value={window.end}
                          aria-invalid={Boolean(issues[index])}
                          onChange={(event) => {
                            const end = event.currentTarget.value
                            setWindows((current) =>
                              current.map((item) =>
                                item.key === window.key
                                  ? { ...item, end }
                                  : item
                              )
                            )
                          }}
                        />
                      </label>
                      {windows.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          className={styles.removeTime}
                          aria-label={`Remove time ${index + 1}`}
                          title="Remove time"
                          onClick={() =>
                            setWindows((current) =>
                              current.filter((item) => item.key !== window.key)
                            )
                          }
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </Button>
                      ) : null}
                    </div>
                    {issues[index] ? (
                      <small className={panel.fieldError} role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        {issues[index]}
                      </small>
                    ) : null}
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="ghost"
                className={styles.addTime}
                disabled={windows.length >= AGE_SCHEDULING_MAX_WINDOWS}
                onClick={() =>
                  setWindows((current) => [
                    ...current,
                    { key: newKey(), ...suggestedWindow(current, clinic) },
                  ])
                }
              >
                <Plus size={15} aria-hidden="true" /> Add time
              </Button>
              <div className={styles.dialogTimeline}>
                <AgeTimeline windows={windows} range={range} clinic={clinic} />
                <AgeTimelineAxis range={range} />
              </div>
              {outsideClinic ? (
                <p className={styles.outsideNote}>
                  <Info size={14} aria-hidden="true" />
                  Part of these times is outside clinic hours, so Flio won’t
                  book then.
                </p>
              ) : null}
            </fieldset>
          </div>
        )}

        <footer className={panel.dialogFooter}>
          {mode === 'delete' && editing ? (
            <div className={panel.footerActions}>
              <Button
                ref={safeChoice}
                type="button"
                variant="secondary"
                onClick={() => setMode('edit')}
              >
                Keep group
              </Button>
              <Button
                type="button"
                className={panel.dangerSolid}
                onClick={() => onApply(deleteGroup(groups, editing.key))}
              >
                Delete group
              </Button>
            </div>
          ) : mode === 'discard' ? (
            <>
              <p className={panel.dialogNote} role="alert">
                Discard your changes to this group?
              </p>
              <div className={panel.footerActions}>
                <Button
                  ref={safeChoice}
                  type="button"
                  variant="secondary"
                  onClick={() => setMode('edit')}
                >
                  Keep editing
                </Button>
                <Button type="button" onClick={onCancel}>
                  Discard changes
                </Button>
              </div>
            </>
          ) : (
            <>
              {editing ? (
                <div className={styles.deleteAction}>
                  <Button
                    type="button"
                    variant="secondary"
                    className={panel.dangerOutline}
                    disabled={Boolean(deleteBlocked)}
                    aria-describedby={
                      deleteBlocked ? ids.deleteHint : undefined
                    }
                    onClick={() => ask('delete')}
                  >
                    <Trash2 size={15} aria-hidden="true" /> Delete group
                  </Button>
                  {deleteBlocked ? (
                    <small id={ids.deleteHint} className={panel.hint}>
                      {deleteBlocked}
                    </small>
                  ) : null}
                </div>
              ) : (
                <p className={panel.dialogNote}>
                  Applied changes are saved with Save age-based scheduling.
                </p>
              )}
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={requestClose}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!result}
                  onClick={() => {
                    if (result) onApply(result)
                  }}
                >
                  {editing ? 'Apply' : 'Add group'}
                </Button>
              </div>
            </>
          )}
        </footer>
      </section>
    </div>,
    document.body
  )
}
